import { cn } from "@/lib/utils";

type CartProductRecommendationSkeletonProps = {
  itemCount?: number;
};

const ProductCardSkeleton = ({ className }: { className?: string }) => {
  return (
    <article className={cn("group/card relative w-full rounded-md", className)}>
      <div>
        <figure className="bg-product-card relative flex h-[15.625rem] w-full items-center justify-center gap-2 overflow-hidden rounded-md md:h-[22.625rem]">
          <div className="relative flex size-[9.625rem] items-center justify-center md:size-56">
            <div className="shimmer absolute inset-0 rounded-md" />
          </div>
          <div className="absolute top-3 left-2.5 z-20 md:top-3.5 md:left-4">
            <div className="shimmer h-5 w-5.5 rounded-full md:h-8 md:w-8" />
          </div>
          <div className="absolute top-3 right-2.5 z-20 md:top-6 md:right-2.5">
            <div className="shimmer h-[15px] w-16 rounded-xs md:h-[20px] md:w-20" />
          </div>
          <div className="bg-white-primary absolute right-1 bottom-1 left-1 z-20 flex h-8 items-center justify-between gap-1.5 rounded-sm px-1 py-1 md:right-2.5 md:bottom-2 md:left-2.5 md:h-12.5 md:gap-2 md:rounded-md md:px-2 md:py-1.25">
            <div className="shimmer h-4 w-8 rounded md:h-5 md:w-10" />
            <div className="bg-product-card flex h-[26px] grow items-center justify-between rounded-sm md:h-10">
              <div className="shimmer mx-2 h-3 w-3 rounded md:h-4 md:w-4" />
              <div className="shimmer h-4 w-4 rounded" />
              <div className="shimmer mx-2 h-3 w-3 rounded md:h-4 md:w-4" />
            </div>
            <div className="shimmer h-6.5 w-11 rounded-xs md:h-10 md:w-[4.625rem]" />
          </div>
        </figure>
        <div className="w-full space-y-2 pt-3 pb-1 md:px-2.5">
          <div className="flex w-full items-start justify-between gap-1.25 font-medium">
            <div className="flex-1 space-y-2">
              <div className="shimmer h-6 w-3/4 rounded md:h-8" />
              <div className="shimmer h-6 w-1/2 rounded md:hidden md:h-8" />
            </div>
            <div className="shimmer h-8 w-12 rounded md:h-8 md:w-16" />
          </div>
          <dl className="text-primary-light flex items-center justify-between gap-1.25">
            <div className="flex items-center gap-1.25">
              <div className="shimmer hidden h-[10px] w-16 rounded md:inline-block" />
            </div>
          </dl>
        </div>
      </div>
    </article>
  );
};

const CartProductRecommendationSkeleton = ({ itemCount = 4 }: CartProductRecommendationSkeletonProps) => {
  const skeletonItems = Array.from({ length: itemCount }, (_, index) => index);

  return (
    <section className="space-y-7.5 py-6 max-sm:px-4 md:py-12" aria-labelledby="loading-recommendations">
      <header className="container flex flex-row flex-wrap items-center justify-center gap-3 px-4 sm:justify-between md:px-7.5">
        <div className="shimmer h-8 w-64 rounded md:h-10 md:w-80" />
        <div className="shimmer h-6 w-32 rounded max-sm:hidden" />
      </header>
      <div className="container grid gap-4.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:px-7.5">
        {skeletonItems.map((_, index) => (
          <ProductCardSkeleton
            key={index}
            className={cn("max-w-full md:max-w-none", itemCount > 4 && "xl:max-w-[21.25rem]")}
          />
        ))}
      </div>
    </section>
  );
};

export default CartProductRecommendationSkeleton;
